import React from "react";
import { type tryTemplateActions } from "../components/TryTemplateButton/TryTemplateButton";
import { Alert, AlertDescription } from "@zapier/design-system";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { TemplateCreationModal } from "../components/TemplateCreationModal";
import styles from "../components/TryTemplateButton/TryTemplateButton.module.css";
import {
  COPILOT_PROMPT,
  COPILOT_QUERY_PARAM,
} from "src/components/TryTemplateButton";
import {
  emitUserInteractionEvent,
  ENTRY_POINT_LOCATION,
  ENTRY_POINT_LOCATION_QUERY_PARAM,
  ENTRY_POINT_METHOD_QUERY_PARAM,
  UserInteractionEventName,
} from "src/observability/tracking";
import { Template } from "src/types";

export const useTemplateCreation = ({
  tryTemplateActions,
  webIntentUrl,
  slug,
  products,
}: {
  tryTemplateActions: tryTemplateActions;
  webIntentUrl?: string;
  slug: string;
  products: Template["products"];
}) => {
  const { createTemplate, hasCreationScreen, creationError, setCreationError } =
    tryTemplateActions;
  const isWebIntent = !!webIntentUrl?.trim();

  const createTemplateAndNavigate = ({
    slug,
    webIntentUrl,
    createTemplate,
    onClick,
  }: {
    slug: string;
    webIntentUrl: string | undefined;
    createTemplate: (slug: string) => void;
    onClick?: () => void;
  }) => {
    emitUserInteractionEvent(UserInteractionEventName.templateCreateClicked, {
      event_action: "click",
      option_selected: slug,
    });

    if (onClick) {
      onClick();
      return;
    }

    if (webIntentUrl) {
      const url = new URL(webIntentUrl, window.location.origin);
      url.searchParams.set(COPILOT_QUERY_PARAM, COPILOT_PROMPT);
      // Setting the entry point to discover_template allows us to take advantage of the
      // opt out of deriving the entry point from the URL. It will always be set to `zap_template`
      // See MR for context: https://gitlab.com/zapier/zapier/-/merge_requests/61220
      url.searchParams.set(ENTRY_POINT_METHOD_QUERY_PARAM, "discover_template");
      url.searchParams.set(
        ENTRY_POINT_LOCATION_QUERY_PARAM,
        ENTRY_POINT_LOCATION,
      );

      window.location.href = url.href;
      return;
    }
    createTemplate(slug);
  };

  const renderCreationUI = () => (
    <React.Fragment>
      {!isWebIntent &&
        creationError &&
        createPortal(
          <div className={classNames(styles.alertContainer)}>
            <Alert
              onDismiss={() => {
                setCreationError(null);
              }}
              status="error"
              variant="alert"
              title="Error"
            >
              <AlertDescription>{creationError}</AlertDescription>
            </Alert>
          </div>,
          document.body,
        )}
      {!isWebIntent && hasCreationScreen && slug && products && (
        <TemplateCreationModal slug={slug} products={products} />
      )}
    </React.Fragment>
  );

  return {
    createTemplate,
    hasCreationScreen,
    creationError,
    setCreationError,
    isWebIntent,
    renderCreationUI,
    createTemplateAndNavigate,
  };
};
